import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, MapPin, Clock, Phone, Mail, ChevronLeft, ChevronRight, Heart, Shield, Users, Award, Stethoscope, Star, Quote, CheckCircle } from 'lucide-react';
import Autoplay from 'embla-carousel-autoplay';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const makeSlug = (n) => (n || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.15 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {children}
    </div>
  );
}

export default function Home() {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/blogs`).then(r => setBlogs(r.data)).catch(() => {});
    axios.get(`${API}/site-content`).then(r => setSiteContent(r.data)).catch(() => {});
    axios.post(`${API}/analytics/visit`, { page: '/' }).catch(() => {});
  }, []);

  const onSelect = useCallback(() => {
    if (!carouselApi) return;
    setCurrentSlide(carouselApi.selectedScrollSnap());
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;
    carouselApi.on('select', onSelect);
    return () => carouselApi.off('select', onSelect);
  }, [carouselApi, onSelect]);

  const slides = siteContent.carousel_slides || [];
  const hours = siteContent.opening_hours || {};

  const whyChooseUs = [
    { icon: Shield, title: 'Your Visit Stays Private', desc: 'Nothing leaves this clinic. Your records, your questions, your reasons — all of it is kept between you and the doctor who sees you.' },
    { icon: Award, title: 'Doctors Who Listen', desc: 'Dr. Reenu and her team are licensed gynecologists who spend the time to actually talk through what is going on, not rush you out.' },
    { icon: Heart, title: 'No Judgment, Ever', desc: 'We have heard every story. You will not be lectured, made to feel small, or asked to explain yourself more than you want to.' },
    { icon: Stethoscope, title: 'Properly Equipped', desc: 'On-site ultrasound, lab work, and modern exam rooms — so most things can be handled in one visit instead of three.' },
    { icon: Users, title: 'Since 2011', desc: 'Over 5,000 women from across the GTA have walked through our door. Most come back, and most send a friend.' },
    { icon: Clock, title: 'Wednesday Late Nights', desc: 'We stay open until 7 PM on Wednesdays and run Saturday morning slots so you do not have to take a day off work.' },
  ];

  const testimonials = [
    { name: 'Marie L.', text: 'I was honestly dreading the appointment. The front desk was kind, Dr. Reenu didn\'t rush me, and I left actually feeling okay about what came next. That meant a lot.', rating: 5 },
    { name: 'Sophie B.', text: 'Came in for a second opinion after a bad experience elsewhere. They explained the ultrasound to me in plain English and didn\'t push anything I wasn\'t comfortable with.', rating: 5 },
    { name: 'Anna K.', text: 'Brought my teenage daughter here for her first visit. She was nervous, and the nurse really took her time. We will be coming back.', rating: 5 },
  ];

  const services = [
    { title: 'Annual Gynecological Exam', desc: 'Pap smear, pelvic exam, breast check — the routine stuff that catches things early.', link: '/gynecology' },
    { title: 'Birth Control & Family Planning', desc: 'IUDs, pills, patches, implants. We will talk through what fits your life, not just what is easiest to prescribe.', link: '/gynecology' },
    { title: 'Medical Abortion (Up to 9 Weeks)', desc: 'Health Canada approved pills with full medical oversight before and after.', link: '/abortion' },
    { title: 'Pregnancy Care', desc: 'Confirmation visits, early ultrasounds, prenatal monitoring, and referrals when needed.', link: '/gynecology' },
    { title: 'STI Testing & Treatment', desc: 'Walk-in friendly screening for chlamydia, gonorrhea, HPV, and more. Results in days, not weeks.', link: '/gynecology' },
    { title: 'Perimenopause & Menopause', desc: 'Hot flashes, sleep issues, mood changes — practical options including HRT when it makes sense.', link: '/gynecology' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Carousel */}
      <section className="relative" data-testid="hero-carousel-section">
        <Carousel opts={{ loop: true, align: 'start' }} plugins={[Autoplay({ delay: 5000, stopOnInteraction: false })]} setApi={setCarouselApi} className="w-full">
          <CarouselContent className="-ml-0">
            {slides.map((slide, i) => (
              <CarouselItem key={i} className="pl-0">
                <div className="hero-slide relative h-[500px] sm:h-[520px] md:h-[600px] lg:h-[680px] overflow-hidden">
                  <img src={slide.image} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} />
                  <div className="hero-slide-content absolute inset-0 flex items-start pt-16 sm:items-center sm:pt-0">
                    <div className="w-full px-5 sm:px-8 md:px-12 lg:px-16 max-w-7xl mx-auto">
                      <div className="max-w-[90%] sm:max-w-xl lg:max-w-2xl">
                        <div className="accent-line mb-4 sm:mb-6" />
                        <h1 className="font-heading text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.15]" data-testid={`hero-title-${i}`}>
                          {slide.title}
                        </h1>
                        <p className="mt-3 sm:mt-5 text-[0.8rem] sm:text-base md:text-lg text-white/85 leading-relaxed max-w-sm sm:max-w-md lg:max-w-lg">{slide.subtitle}</p>
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-5 sm:mt-8">
                          <Link to={slide.cta_link || '/contact'}>
                            <Button className="rounded-full px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base shadow-lg w-full sm:w-auto" data-testid={`hero-cta-${i}`}>
                              {slide.cta_text} <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                          <Link to="/about">
                            <Button variant="ghost" className="rounded-full px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base text-white bg-white/10 hover:bg-white/20 hover:text-white w-full sm:w-auto backdrop-blur-sm" data-testid={`hero-secondary-${i}`}>
                              Learn More
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 sm:gap-4">
            <button onClick={() => carouselApi?.scrollPrev()} className="hidden sm:flex w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm items-center justify-center hover:bg-white/25 transition-all" data-testid="carousel-prev">
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button key={i} onClick={() => carouselApi?.scrollTo(i)} className={`carousel-dot ${currentSlide === i ? 'active' : ''}`} data-testid={`carousel-dot-${i}`} />
              ))}
            </div>
            <button onClick={() => carouselApi?.scrollNext()} className="hidden sm:flex w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm items-center justify-center hover:bg-white/25 transition-all" data-testid="carousel-next">
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </div>
        </Carousel>
      </section>

      {/* Quick Services Bar */}
      <AnimatedSection>
        <section className="relative -mt-10 sm:-mt-16 z-10 px-4 sm:px-6 md:px-8" data-testid="quick-services-bar">
          <div className="container mx-auto">
            {(() => {
              const phones = (siteContent.phones && siteContent.phones.length ? siteContent.phones : [siteContent.phone || '+1 548-532-2279']).filter(Boolean);
              const addresses = (siteContent.addresses && siteContent.addresses.length ? siteContent.addresses : [siteContent.address || 'Brampton, ON']).filter(Boolean);
              const hoursMonFri = (siteContent.opening_hours && siteContent.opening_hours.Monday) || '8:00 AM - 6:00 PM';
              const email = siteContent.email || 'info@drrenuclinic.com';
              return (
                <div className="bg-card rounded-2xl shadow-xl border border-border/50 p-4 sm:p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-testid="quick-info-grid">
                  {/* Hours tile */}
                  <Link to="/contact" className="flex items-start gap-2 sm:gap-3 group hover:opacity-80 transition-opacity" data-testid="quick-info-hours">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-heading font-medium">Mon-Fri</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">{hoursMonFri}</p>
                    </div>
                  </Link>

                  {/* Phones tile — supports multiple numbers */}
                  <div className="flex items-start gap-2 sm:gap-3" data-testid="quick-info-phones">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-heading font-medium">Call Us</p>
                      {phones.map((ph, i) => (
                        <a key={i} href={`tel:${ph.replace(/[^+\d]/g, '')}`} className="block text-[10px] sm:text-xs text-muted-foreground hover:text-primary transition-colors truncate" data-testid={`quick-info-phone-${i}`}>{ph}</a>
                      ))}
                    </div>
                  </div>

                  {/* Addresses tile — supports multiple locations */}
                  <Link to="/contact" className="flex items-start gap-2 sm:gap-3 group hover:opacity-80 transition-opacity" data-testid="quick-info-addresses">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-heading font-medium">Visit Us</p>
                      {addresses.map((a, i) => (
                        <p key={i} className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2" data-testid={`quick-info-address-${i}`}>{a}</p>
                      ))}
                    </div>
                  </Link>

                  {/* Email tile */}
                  <a href={`mailto:${email}`} className="flex items-start gap-2 sm:gap-3 group hover:opacity-80 transition-opacity" data-testid="quick-info-email">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-heading font-medium">Email</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{email}</p>
                    </div>
                  </a>
                </div>
              );
            })()}
          </div>
        </section>
      </AnimatedSection>

      {/* Products Slider Section */}
      <AnimatedSection>
        <section className="py-20 sm:py-28" data-testid="home-products-section">
          <div className="container mx-auto px-6 md:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">Our Products</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">{t('products.title')}</h2>
              <div className="accent-line mx-auto mt-4" />
              <p className="text-base text-muted-foreground mt-4 max-w-xl mx-auto">{t('products.subtitle')}</p>
            </div>
            <Carousel opts={{ loop: true, align: 'start' }} plugins={[Autoplay({ delay: 4000, stopOnInteraction: true })]} className="w-full">
              <CarouselContent className="-ml-4">
                {products.map((product, i) => (
                  <CarouselItem key={product.id} className="pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <Link to={`/products/${makeSlug(product.name)}`} className="block h-full" data-testid={`product-card-link-${i}`}>
                      <Card className="hover-float overflow-hidden border border-border rounded-2xl group relative card-shimmer h-full cursor-pointer" data-testid={`product-card-${i}`}>
                        <div className="aspect-[4/3] overflow-hidden bg-muted relative">
                          <img src={product.image_url} alt={product.image_alt || product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                          <div className="absolute top-3 left-3">
                            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-primary shadow-sm">
                              {product.in_stock ? t('products.inStock') : t('products.outOfStock')}
                            </span>
                          </div>
                        </div>
                        <CardContent className="p-5 sm:p-6">
                          <h3 className="font-heading text-lg font-medium group-hover:text-primary transition-colors">{product.name}</h3>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{product.short_description}</p>
                          <span className="inline-flex items-center mt-4 text-sm font-medium text-primary group-hover:gap-2 transition-all" data-testid={`product-view-${i}`}>
                            {t('products.viewDetails')} <ArrowRight className="ml-1 w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
            <div className="text-center mt-10">
              <Link to="/products">
                <Button variant="outline" className="rounded-full px-8 pulse-glow" data-testid="view-all-products-btn">
                  View All Products <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Why Choose Us */}
      <AnimatedSection>
        <section className="py-24 sm:py-32 bg-secondary/40" data-testid="why-choose-section">
          <div className="container mx-auto px-6 md:px-8">
            <div className="text-center mb-16">
              <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">Why Dr. Reenu</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">Why Choose Us</h2>
              <div className="accent-line mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
              {whyChooseUs.map((item, i) => (
                <Card key={i} className="transition-card rounded-2xl border-border bg-card" data-testid={`why-card-${i}`}>
                  <CardContent className="p-8">
                    <div className="icon-glow w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                      <item.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-heading text-lg font-medium">{item.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* About Section */}
      <AnimatedSection>
        <section className="py-24 sm:py-32" data-testid="home-about-section">
          <div className="container mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img src={siteContent.about_image || 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=800&q=80'} alt="About" className="w-full h-[420px] lg:h-[520px] object-cover" />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground rounded-2xl p-8 shadow-xl hidden md:block">
                  <p className="text-4xl font-heading font-bold">15+</p>
                  <p className="text-sm opacity-80 mt-1">{t('about.yearsExp')}</p>
                </div>
                <div className="absolute -top-4 -left-4 bg-accent rounded-2xl p-6 shadow-lg hidden md:block">
                  <p className="text-3xl font-heading font-bold text-accent-foreground">5000+</p>
                  <p className="text-xs text-accent-foreground/70">{t('about.patients')}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('about.title')}</p>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">{siteContent.about_title || 'Dr. Reenu Women\'s Clinic'}</h2>
                <div className="accent-line mt-4" />
                <p className="text-base text-muted-foreground mt-6 leading-relaxed whitespace-pre-line line-clamp-[8]">{siteContent.about_content?.split('\n').slice(0, 3).join('\n')}</p>
                <div className="grid grid-cols-3 gap-4 mt-8">
                  {[
                    { val: '98%', label: t('about.satisfaction') },
                    { val: '12+', label: t('about.specialists') },
                    { val: '24/7', label: 'Support' },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-4 bg-muted/50 rounded-2xl border border-border">
                      <p className="text-2xl font-heading font-bold text-primary">{stat.val}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <Link to="/about">
                  <Button className="mt-8 rounded-full px-8" data-testid="home-about-cta">{t('about.readMore')} <ArrowRight className="ml-2 w-4 h-4" /></Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Services Grid */}
      <AnimatedSection>
        <section className="py-24 sm:py-32 counter-bg text-white" data-testid="services-section">
          <div className="container mx-auto px-6 md:px-8">
            <div className="text-center mb-16">
              <p className="text-sm text-white/60 uppercase tracking-widest mb-3">What We Offer</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">Our Services</h2>
              <div className="w-16 h-0.5 bg-white/30 mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s, i) => (
                <Link key={i} to={s.link} className="group" data-testid={`service-card-${i}`}>
                  <div className="p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-5 h-5 text-white/70" />
                      <h3 className="font-heading text-lg font-medium">{s.title}</h3>
                    </div>
                    <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
                    <p className="text-sm text-white/40 mt-3 group-hover:text-white/70 transition-colors flex items-center gap-1">
                      Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Testimonials */}
      <AnimatedSection>
        <section className="py-24 sm:py-32" data-testid="testimonials-section">
          <div className="container mx-auto px-6 md:px-8">
            <div className="text-center mb-16">
              <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">Testimonials</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">What Our Patients Say</h2>
              <div className="accent-line mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((test, i) => (
                <Card key={i} className="testimonial-card rounded-2xl border-border" data-testid={`testimonial-card-${i}`}>
                  <CardContent className="p-8">
                    <Quote className="w-8 h-8 text-primary/20 mb-4" />
                    <p className="text-sm text-muted-foreground leading-relaxed italic">"{test.text}"</p>
                    <div className="flex items-center gap-1 mt-4">
                      {[...Array(test.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                    </div>
                    <p className="font-heading font-medium mt-3">{test.name}</p>
                    <p className="text-xs text-muted-foreground">Verified Patient</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Location & Hours */}
      <AnimatedSection>
        <section className="py-24 sm:py-32 bg-secondary/40" data-testid="home-location-section">
          <div className="container mx-auto px-6 md:px-8">
            <div className="text-center mb-16">
              <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('location.title')}</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">{t('location.title')}</h2>
              <div className="accent-line mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Address Card */}
              <Card className="rounded-2xl border-border" data-testid="location-address-card">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-medium mb-3">{t('location.address')}</h3>
                  {(() => {
                    const addrs = (siteContent.addresses && siteContent.addresses.length ? siteContent.addresses : (siteContent.address ? [siteContent.address] : [])).filter(Boolean);
                    const phs = (siteContent.phones && siteContent.phones.length ? siteContent.phones : (siteContent.phone ? [siteContent.phone] : [])).filter(Boolean);
                    return (
                      <>
                        {addrs.map((a, i) => (
                          <p key={i} className="text-sm text-muted-foreground leading-relaxed mt-2" data-testid={`location-address-${i}`}>{addrs.length > 1 ? `${i + 1}. ` : ''}{a}</p>
                        ))}
                        <div className="mt-5 space-y-3">
                          {phs.map((ph, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm" data-testid={`location-phone-${i}`}>
                              <Phone className="w-4 h-4 text-primary" />
                              <a href={`tel:${ph.replace(/[^+\d]/g, '')}`} className="text-muted-foreground hover:text-primary transition-colors">{ph}</a>
                            </div>
                          ))}
                          <div className="flex items-center gap-3 text-sm">
                            <Mail className="w-4 h-4 text-primary" />
                            <a href={`mailto:${siteContent.email}`} className="text-muted-foreground hover:text-primary transition-colors break-all">{siteContent.email}</a>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  <Link to="/contact">
                    <Button variant="outline" className="mt-6 rounded-full" data-testid="location-directions-btn">Get Directions <ArrowRight className="ml-2 w-4 h-4" /></Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Hours Card */}
              <Card className="rounded-2xl border-border md:col-span-1 lg:col-span-2" data-testid="location-hours-card">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-medium mb-5">{t('location.hours')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(hours).map(([day, time]) => {
                      const isToday = new Date().toLocaleDateString('en', { weekday: 'long' }) === day;
                      return (
                        <div key={day} className={`flex justify-between items-center py-3 px-4 rounded-xl transition-colors ${isToday ? 'bg-primary/5 border border-primary/20' : time === 'Closed' ? 'bg-destructive/5' : 'bg-muted/50'}`}>
                          <span className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>
                            {day} {isToday && <span className="text-xs ml-1">(Today)</span>}
                          </span>
                          <span className={`text-sm ${time === 'Closed' ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>{time}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">* Extended hours on Wednesdays. Emergency consultations available by appointment.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Blog Section */}
      <AnimatedSection>
        <section className="py-24 sm:py-32" data-testid="home-blog-section">
          <div className="container mx-auto px-6 md:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-4">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('blog.title')}</p>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">{t('blog.title')}</h2>
                <div className="accent-line mt-4" />
              </div>
              <Link to="/blog" className="hidden md:block">
                <Button variant="outline" className="rounded-full" data-testid="home-blog-cta">View All Articles <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogs.slice(0, 3).map((blog, i) => (
                <Card key={blog.id} className="transition-card overflow-hidden rounded-2xl border-border group" data-testid={`blog-card-${i}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={blog.image_url} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <CardContent className="p-6">
                    <p className="text-xs text-muted-foreground">{t('blog.by')} {blog.author} &bull; {new Date(blog.created_at).toLocaleDateString()}</p>
                    <h3 className="font-heading text-lg font-medium mt-2 line-clamp-2 group-hover:text-primary transition-colors">{blog.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{blog.excerpt}</p>
                    <Link to={`/blog/${blog.slug}`}>
                      <Button variant="ghost" size="sm" className="mt-3 text-sm p-0 h-auto" data-testid={`blog-read-${i}`}>
                        {t('blog.readMore')} <ArrowRight className="ml-1 w-3.5 h-3.5" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-10 text-center md:hidden">
              <Link to="/blog"><Button variant="outline" className="rounded-full">View All Articles <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection>
        <section className="py-24 sm:py-32 bg-primary text-primary-foreground" data-testid="cta-section">
          <div className="container mx-auto px-6 md:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light">Not Sure Where to Start? Just Call.</h2>
            <p className="text-lg opacity-80 mt-5 max-w-xl mx-auto leading-relaxed">A short phone call can clear up most questions. We will book you in, walk you through what the visit looks like, and tell you exactly what to bring — no commitment required.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="rounded-full px-10 text-base shadow-lg" data-testid="cta-book-btn">{t('hero.cta')} <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
              <a href="tel:+15485322279">
                <Button size="lg" variant="outline" className="rounded-full px-10 text-base border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="cta-call-btn">
                  <Phone className="w-4 h-4 mr-2" /> Call Now
                </Button>
              </a>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
}
